import { ReloadOutlined } from '@ant-design/icons';
import type { ReturnItemRow, ReturnRow } from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { formatAmount, formatDisplayTime, formatQty } from '@hq/utils';
import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Alert, Button, Card, Descriptions, Drawer, Segmented, Space, Spin, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useState } from 'react';
import { hq } from '../session';
import { ellipsisCell, sumWidth } from '@hq/ui-kit';

const PAGE_SIZE = 20;

type StatusFilter = 'ALL' | 'SUBMITTED' | 'AUDITED' | 'PICKING' | 'RECEIVED' | 'REFUNDED' | 'EXCHANGED' | 'CLOSED' | 'REJECTED';

const STATUS_META: Record<string, { text: string; color: string }> = {
  NEW: { text: '草稿', color: 'default' },
  SUBMITTED: { text: '待受理', color: 'gold' },
  AUDITED: { text: '已审核', color: 'processing' },
  PICKING: { text: '取件中', color: 'blue' },
  RECEIVED: { text: '已回仓', color: 'cyan' },
  CHECKED: { text: '已判定', color: 'cyan' },
  REFUNDED: { text: '已退款', color: 'green' },
  EXCHANGED: { text: '已换货', color: 'green' },
  CLOSED: { text: '已关闭', color: 'default' },
  REJECTED: { text: '已驳回', color: 'red' }
};

const REASON_META: Record<string, string> = {
  QUALITY: '质量问题',
  WRONG_ITEM: '错发/错送',
  NEAR_EXPIRY: '近效期',
  DAMAGE: '破损',
  OVER_ORDER: '超量配送',
  POLICY: '政策原因'
};

const RETURN_TYPE_META: Record<string, string> = {
  RETURN_REFUND: '退货退款',
  EXCHANGE: '换货',
  DISCOUNT: '折让'
};

const FILTERS: { label: string; value: StatusFilter }[] = [
  { label: '全部', value: 'ALL' },
  { label: '待受理', value: 'SUBMITTED' },
  { label: '已审核', value: 'AUDITED' },
  { label: '取件中', value: 'PICKING' },
  { label: '已回仓', value: 'RECEIVED' },
  { label: '已退款', value: 'REFUNDED' },
  { label: '已换货', value: 'EXCHANGED' },
  { label: '已关闭', value: 'CLOSED' },
  { label: '已驳回', value: 'REJECTED' }
];

/**
 * 退换货（供应商端）：跟踪院内发起的退货/换货单及处理进度。
 * 处理动作（取件、回仓、责任判定、驳回、关单）由院内角色执行，
 * 对应端点尚未配置供应商侧权限码，此处仅提供只读跟踪，避免越权操作。
 */
export default function ReturnSupplier() {
  const [status, setStatus] = useState<StatusFilter>('ALL');
  const [currentId, setCurrentId] = useState<string | null>(null);

  const query = useInfiniteQuery({
    queryKey: ['supplier', 'return', 'list', status],
    placeholderData: keepPreviousData,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      hq.returns.list({
        lastId: pageParam,
        size: PAGE_SIZE,
        returnStatus: status === 'ALL' ? undefined : status
      }),
    // hasMore 为真但 nextLastId 为空时不能把 null 当成游标：否则会重复拉首页
    getNextPageParam: (lastPage) => (lastPage.hasMore && lastPage.nextLastId ? lastPage.nextLastId : undefined)
  });

  const detail = useQuery({
    queryKey: ['supplier', 'return', 'detail', currentId],
    placeholderData: keepPreviousData,
    queryFn: () => hq.returns.detail(currentId ?? ''),
    enabled: Boolean(currentId)
  });

  const rows = query.data?.pages.flatMap((page) => page.items) ?? [];

  const columns: ColumnsType<ReturnRow> = [
    {
      title: '退换货单号',
      dataIndex: 'bizNo',
      width: 210,
      render: (value: string, row) => <Typography.Link onClick={() => setCurrentId(row.id)}>{value}</Typography.Link>
    },
    {
      title: '类型',
      dataIndex: 'returnType',
      width: 110,
      render: (value: string) => RETURN_TYPE_META[value] ?? value
    },
    {
      title: '原因',
      dataIndex: 'reasonType',
      width: 120,
      render: (value: string | null) => (value ? REASON_META[value] ?? value : '-')
    },
    { title: '来源单号', dataIndex: 'sourceBizNo', width: 190, render: (value: string | null) => value || '-' },
    {
      title: '退货科室',
      dataIndex: 'applyDeptName',
      width: 150,
      // 内部雪花 ID 不是业务信息：无名称时显示占位，绝不回退成 apply_dept_id
      render: (value: string | null) => value || '-'
    },
    { title: '数量', dataIndex: 'totalQty', width: 100, align: 'right', render: (value: number) => formatQty(value) },
    {
      title: '退货金额',
      dataIndex: 'returnAmount',
      width: 130,
      align: 'right',
      render: (value: number) =>
        value < 0 ? <Typography.Text type="danger">{formatAmount(value)}</Typography.Text> : formatAmount(value)
    },
    {
      title: '状态',
      dataIndex: 'returnStatus',
      width: 110,
      render: (value: string) => {
        const meta = STATUS_META[value] ?? { text: value, color: 'default' };
        return <Tag color={meta.color}>{meta.text}</Tag>;
      }
    },
    { title: '申请时间', dataIndex: 'bizDate', width: 160, render: (value: string) => formatDisplayTime(value) }
  ];

  const itemColumns: ColumnsType<ReturnItemRow> = [
    { title: '行', dataIndex: 'lineNo', width: 60 },
    { title: '物资编码', dataIndex: 'materialCode', width: 150, render: (value: string | null) => value || '-' },
    { title: '物资名称', dataIndex: 'materialName', ellipsis: { showTitle: false }, render: ellipsisCell('materialName') },
    { title: '批次', dataIndex: 'batchNo', width: 120, render: (value: string | null) => value || '-' },
    { title: '采购数量', dataIndex: 'buyQty', width: 100, align: 'right', render: (value: number) => formatQty(value) },
    { title: '退货数量', dataIndex: 'returnQty', width: 100, align: 'right', render: (value: number) => formatQty(value) },
    { title: '实收数量', dataIndex: 'receivedQty', width: 100, align: 'right', render: (value: number) => formatQty(value) },
    { title: '单价', dataIndex: 'price', width: 110, align: 'right', render: (value: number) => formatAmount(value) },
    {
      title: '金额',
      dataIndex: 'returnAmount',
      width: 120,
      align: 'right',
      render: (value: number) => formatAmount(value)
    },
    { title: '质量问题', dataIndex: 'qualityDesc', ellipsis: { showTitle: false }, render: ellipsisCell('qualityDesc') }
  ];

  return (
    <Card
      className="page-card"
      title="退换货"
      extra={
        <Button icon={<ReloadOutlined />} onClick={() => query.refetch()} loading={query.isFetching}>
          刷新
        </Button>
      }
    >
      <Segmented<StatusFilter>
        value={status}
        options={FILTERS}
        onChange={(value) => setStatus(value)}
        style={{ marginBottom: 12 }}
      />

      {query.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="退换货单加载失败"
          description={query.error instanceof ApiError ? query.error.friendlyMessage : String(query.error)}
        />
      ) : null}

      <Table<ReturnRow>
        rowKey="id"
        size="middle"
        columns={columns}
        dataSource={rows}
        loading={query.isPending}
        pagination={false}
        scroll={{ x: sumWidth(columns) }}
        locale={{ emptyText: query.isError ? '加载失败，请重试' : '暂无退换货单' }}
        onRow={(row) => ({ onDoubleClick: () => setCurrentId(row.id) })}
      />

      <Space style={{ marginTop: 16 }}>
        <Typography.Text type="secondary">已加载 {rows.length} 条</Typography.Text>
        {query.hasNextPage ? (
          <Button onClick={() => query.fetchNextPage()} loading={query.isFetchingNextPage}>
            加载更多
          </Button>
        ) : null}
      </Space>

      <Drawer
        title={detail.data?.returnOrder.bizNo ?? '退换货详情'}
        open={currentId !== null}
        onClose={() => setCurrentId(null)}
        width={900}
      >
        {detail.isLoading ? (
          <Spin />
        ) : detail.isError ? (
          <Alert
            type="error"
            showIcon
            message="详情加载失败"
            description={detail.error instanceof ApiError ? detail.error.friendlyMessage : String(detail.error)}
          />
        ) : detail.data ? (
          <>
            <Descriptions size="small" column={2} bordered style={{ marginBottom: 16 }}>
              <Descriptions.Item label="状态">
                {STATUS_META[detail.data.returnOrder.returnStatus]?.text ?? detail.data.returnOrder.returnStatus}
              </Descriptions.Item>
              <Descriptions.Item label="类型">
                {RETURN_TYPE_META[detail.data.returnOrder.returnType] ?? detail.data.returnOrder.returnType}
              </Descriptions.Item>
              <Descriptions.Item label="原因">
                {detail.data.returnOrder.reasonType
                  ? REASON_META[detail.data.returnOrder.reasonType] ?? detail.data.returnOrder.reasonType
                  : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="来源单号">{detail.data.returnOrder.sourceBizNo || '-'}</Descriptions.Item>
              {/* 与列表列同一口径：后端 GetAsync 已回填 applyDeptName，绝不展示内部雪花 ID */}
              <Descriptions.Item label="退货科室">{detail.data.returnOrder.applyDeptName || '-'}</Descriptions.Item>
              <Descriptions.Item label="供应商">{detail.data.returnOrder.supplierName || '-'}</Descriptions.Item>
              <Descriptions.Item label="数量合计">{formatQty(detail.data.returnOrder.totalQty)}</Descriptions.Item>
              <Descriptions.Item label="退货金额">{formatAmount(detail.data.returnOrder.returnAmount)}</Descriptions.Item>
              <Descriptions.Item label="处理结果" span={2}>
                {detail.data.returnOrder.handleResult || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="备注" span={2}>
                {detail.data.returnOrder.remark || '-'}
              </Descriptions.Item>
            </Descriptions>
            <Table<ReturnItemRow>
              rowKey="id"
              size="small"
              columns={itemColumns}
              dataSource={detail.data.items}
              pagination={false}
              scroll={{ x: sumWidth(itemColumns) }}
            />
          </>
        ) : null}
      </Drawer>
    </Card>
  );
}
